import AddToHomeScreenIcon from '@mui/icons-material/AddToHomeScreen';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CropFreeIcon from '@mui/icons-material/CropFree';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import HomeIcon from '@mui/icons-material/Home';
import InfoIcon from '@mui/icons-material/Info';
import NfcIcon from '@mui/icons-material/Nfc';
import StarIcon from '@mui/icons-material/Star';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import React from 'react';
import { useAuth } from 'react-oidc-context';

import { Event, Key, Person, Search } from '@mui/icons-material';
import { Locale } from '../../translations/translations';
import { isIosSafari, isProductionEnvironment, isStandalone } from '../../utils/utils';
import { useEndpointsData } from '../EndpointsData/EndpointsDataProvider';
import { useEnvironmentData } from '../EnvironmentProvider/EnvironmentProvider';
import { useHomeData } from '../LoadHomeData/HomeDataProvider';
import { HeaderHandleLanguage } from './HeaderHandleLanguage';
import { HeaderListItem } from './HeaderListItem';

interface HeaderDrawerContentProps {
    translations: Record<string, string>;
    onClose: () => void;
    onAbout: () => void;
}

export const HeaderDrawerContent: React.FC<HeaderDrawerContentProps> = ({ translations, onClose, onAbout }) => {
    const { signoutRedirect: logoutHandler } = useAuth();
    const { homepageData, currentLanguage, setLanguage } = useHomeData();
    const { hideInProduction, setHideInProduction } = useEnvironmentData();
    const { endpointsData } = useEndpointsData();
    const isGuard = homepageData?.authorizations?.mayUseGuardsMobileFx ?? false;

    const handleLogout = () => {
        logoutHandler();
    };

    const handleLanguage = (lang: Locale) => {
        setLanguage(lang);
        onClose();
    };

    return (
        <Grid container direction="column" justifyContent="space-between" wrap="nowrap" style={{ height: '100%' }}>
            {homepageData?.siteAccessSituation && (
                <div role="presentation" onClick={onClose}>
                    <List dense>
                        <HeaderListItem url="/" Icon={<HomeIcon />} text={translations.MENU_LINK_HOME} />
                        {!isStandalone && isIosSafari && (
                            <HeaderListItem
                                url="/help/ios/add-to-homescreen"
                                Icon={<AddToHomeScreenIcon />}
                                text={translations.MENU_LINK_ADD}
                            />
                        )}
                        <HeaderListItem
                            url="/my-data"
                            Icon={<Person />}
                            text={translations.MENU_LINK_MY_DATA}
                        ></HeaderListItem>
                        <HeaderListItem
                            url="/favourites"
                            Icon={<StarIcon />}
                            text={translations.MENU_LINK_MY_FAVOURITES}
                        />
                        <HeaderListItem
                            url="/list-access-points"
                            Icon={<Search />}
                            text={translations.MENU_LINK_SEARCH_ACCESS_POINT}
                        />
                        <HeaderListItem url="/access-to-accelerators" Icon={<Key />} text={translations.ATA_TITLE} />
                        {endpointsData.featureFlags.EVACUATION_EVENTS && (
                            <HeaderListItem
                                url="/evacuations"
                                Icon={<Event />}
                                text={translations.MENU_LINK_EVACUATIONS}
                            />
                        )}{' '}
                    </List>
                    {isGuard && (
                        <>
                            <Divider />
                            <List dense>
                                <HeaderListItem
                                    url="/functions/cern-access"
                                    Icon={<NfcIcon />}
                                    text={translations.MENU_LINK_SCAN_NFC}
                                />
                                <HeaderListItem
                                    url="/functions/access-emulation"
                                    Icon={<AssignmentIndIcon />}
                                    text={translations.MENU_LINK_EMULATE_ACCESS_POINT}
                                />
                                <HeaderListItem
                                    url="/functions/validate-qr"
                                    Icon={<CropFreeIcon />}
                                    text={translations.MENU_LINK_VALIDATE_QR_CODE}
                                />
                            </List>
                        </>
                    )}
                    {!isProductionEnvironment() && (
                        <>
                            <Divider />
                            <List dense>
                                <ListItem onClick={() => setHideInProduction(!hideInProduction)}>
                                    <ListItemButton disableRipple>
                                        <ListItemIcon>
                                            {hideInProduction ? <CheckBoxIcon /> : <CheckBoxOutlineBlankIcon />}
                                        </ListItemIcon>
                                        <ListItemText primary="Toggle production UI elements" />
                                    </ListItemButton>
                                </ListItem>
                            </List>
                        </>
                    )}
                    <Divider />
                    <List dense>
                        <ListItem onClick={onAbout} aria-label="about">
                            <ListItemButton disableRipple>
                                <ListItemIcon>
                                    <InfoIcon />
                                </ListItemIcon>
                                <ListItemText primary={translations.MENU_LINK_ABOUT} />
                            </ListItemButton>
                        </ListItem>
                        <ListItem onClick={handleLogout} aria-label="logout">
                            <ListItemButton disableRipple>
                                <ListItemIcon>
                                    <ExitToAppIcon />
                                </ListItemIcon>
                                <ListItemText primary={translations.MENU_LINK_LOGOUT} />
                            </ListItemButton>
                        </ListItem>
                    </List>
                </div>
            )}
            <HeaderHandleLanguage language={currentLanguage} selectLanguage={handleLanguage} />
        </Grid>
    );
};
